import { ViewConfig } from "@lib/views";
import { TaskColumn, TaskSortValue } from "@common/types/views/tasksView";
import { TitleCell } from "./TaskView/Cells/TitleCell";
import { DueDateCell } from "./TaskView/Cells/DueDateCell";
import { EstimateCell } from "./TaskView/Cells/EstimateCell";
import { ActionsCell } from "./TaskView/Cells/ActionsCell";
import { EntitiesCell } from "./TaskView/Cells/EntitiesCell";
import { AssigneeCell } from "./TaskView/Cells/AssigneeCell";
import { PriorityCell } from "./TaskView/Cells/PriorityCell";
import { NestedTaskRow } from "./types";
import { StatusCell } from "./TaskView/Cells/StatusCell";
import { CreatedAtCell } from "./TaskView/Cells/CreatedAtCell";
import { CreatedByCell } from "./TaskView/Cells/CreatedByCell";
import { UpdatedAtCell } from "./TaskView/Cells/UpdatedAtCell";
import { GroupCell } from "./TaskView/Cells/GroupCell";
import { CloseStageCell } from "./TaskView/Cells/CloseStageCell";
import { CloseCell } from "./TaskView/Cells/CloseCell";
import { ServicePeriodCell } from "./TaskView/Cells/ServicePeriodCell";
import { VisibilityCell } from "./TaskView/Cells/VisibilityCell";
import { CompletedByCell } from "./TaskView/Cells/CompletedByCell";
import { CompletedAtCell } from "./TaskView/Cells/CompletedAtCell";
import { ClientCell } from "./TaskView/Cells/ClientCell";

export const taskViewConfig: ViewConfig<
    NestedTaskRow,
    TaskColumn,
    TaskSortValue
> = {
    columns: {
        [TaskColumn.NAME]: {
            accessorKey: "title",
            name: "Task",
            header: "Task",
            sort: {
                asc: TaskSortValue.NAME_ASC,
                desc: TaskSortValue.NAME_DESC,
            },
            immutable: true,
            cell: TitleCell,
            minSize: 300,
            maxSize: 800,
        },
        [TaskColumn.DUE_DATE]: {
            accessorKey: "dueDate",
            name: "Due date",
            header: "Due date",
            sort: {
                asc: TaskSortValue.DUE_DATE_ASC,
                desc: TaskSortValue.DUE_DATE_DESC,
            },
            cell: DueDateCell,
            minSize: 160,
            maxSize: 160,
        },
        [TaskColumn.ESTIMATE]: {
            accessorKey: "estimatedTime",
            name: "Estimate",
            header: "Estimate",
            sort: {
                asc: TaskSortValue.ESTIMATE_ASC,
                desc: TaskSortValue.ESTIMATE_DESC,
            },
            cell: EstimateCell,
            minSize: 140,
            maxSize: 160,
        },
        [TaskColumn.GROUP]: {
            accessorKey: "groupId",
            name: "Group",
            header: "Group",
            minSize: 180,
            maxSize: 260,
            cell: GroupCell,
        },
        [TaskColumn.ACTIONS]: {
            accessorKey: "actions",
            name: "Actions",
            header: "",
            minSize: 120,
            maxSize: 120,
            immutable: true,
            cell: ActionsCell,
            hiddenFromSettings: true,
        },
        [TaskColumn.ENTITIES]: {
            accessorKey: "entityIds",
            name: "Entities",
            header: "Entities",
            minSize: 120,
            maxSize: 160,
            cell: EntitiesCell,
        },
        [TaskColumn.ASSIGNEE]: {
            accessorKey: "assignee",
            name: "Assignee",
            header: "Assignee",
            sort: {
                asc: TaskSortValue.ASSIGNEE_ASC,
                desc: TaskSortValue.ASSIGNEE_DESC,
            },
            minSize: 140,
            maxSize: 300,
            cell: AssigneeCell,
        },
        [TaskColumn.PRIORITY]: {
            accessorKey: "priority",
            name: "Priority",
            header: "Priority",
            sort: {
                asc: TaskSortValue.PRIORITY_ASC,
                desc: TaskSortValue.PRIORITY_DESC,
            },
            minSize: 160,
            maxSize: 160,
            cell: PriorityCell,
        },
        [TaskColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            sort: {
                asc: TaskSortValue.STATUS_ASC,
                desc: TaskSortValue.STATUS_DESC,
            },
            minSize: 140,
            maxSize: 140,
            cell: StatusCell,
        },
        [TaskColumn.CREATED_AT]: {
            accessorKey: "createdAt",
            name: "Created on",
            header: "Created on",
            sort: {
                asc: TaskSortValue.CREATED_AT_ASC,
                desc: TaskSortValue.CREATED_AT_DESC,
            },
            minSize: 140,
            maxSize: 140,
            cell: CreatedAtCell,
        },
        [TaskColumn.CREATED_BY]: {
            accessorKey: "createdBy",
            name: "Created by",
            header: "Created by",
            sort: {
                asc: TaskSortValue.CREATED_BY_ASC,
                desc: TaskSortValue.CREATED_BY_DESC,
            },
            minSize: 160,
            maxSize: 200,
            cell: CreatedByCell,
        },
        [TaskColumn.UPDATED_AT]: {
            accessorKey: "updatedAt",
            name: "Updated on",
            header: "Updated on",
            sort: {
                asc: TaskSortValue.UPDATED_AT_ASC,
                desc: TaskSortValue.UPDATED_AT_DESC,
            },
            minSize: 140,
            maxSize: 140,
            cell: UpdatedAtCell,
        },
        [TaskColumn.CLOSE_STAGE]: {
            accessorKey: "closeStageId",
            name: "Stage",
            header: "Stage",
            minSize: 160,
            maxSize: 260,
            cell: CloseStageCell,
        },
        [TaskColumn.CLOSE]: {
            accessorKey: "closeId",
            name: "Service",
            header: "Service",
            minSize: 160,
            maxSize: 260,
            cell: CloseCell,
        },
        [TaskColumn.SERVICE_PERIOD]: {
            accessorKey: "closeId",
            name: "Service period",
            header: "Service period",
            minSize: 160,
            maxSize: 240,
            cell: ServicePeriodCell,
        },
        [TaskColumn.VISIBILITY]: {
            accessorKey: "id",
            name: "Visibility",
            header: "Visibility",
            minSize: 120,
            maxSize: 200,
            cell: VisibilityCell,
            immutable: true,
            hiddenFromSettings: true,
        },
        [TaskColumn.COMPLETED_BY]: {
            accessorKey: "lastDoneBy",
            name: "Completed by",
            header: "Completed by",
            sort: {
                asc: TaskSortValue.COMPLETED_BY_ASC,
                desc: TaskSortValue.COMPLETED_BY_DESC,
            },
            minSize: 160,
            maxSize: 200,
            cell: CompletedByCell,
        },
        [TaskColumn.COMPLETED_AT]: {
            accessorKey: "lastDoneAt",
            name: "Completed on",
            header: "Completed on",
            sort: {
                asc: TaskSortValue.COMPLETED_AT_ASC,
                desc: TaskSortValue.COMPLETED_AT_DESC,
            },
            minSize: 140,
            maxSize: 140,
            cell: CompletedAtCell,
        },
        [TaskColumn.CLIENT]: {
            accessorKey: "workspaceId",
            name: "Client",
            header: "Client",
            sort: {
                asc: TaskSortValue.CLIENT_ASC,
                desc: TaskSortValue.CLIENT_DESC,
            },
            minSize: 160,
            maxSize: 260,
            cell: ClientCell,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: TaskColumn.NAME,
        childrenKey: "tasks",
        hideDisclosureOnLeafChildren: true,
    },
};
